import type { RouteDirection } from './RouteTransition';

const parts = (path: string) => path.split('/').filter(Boolean);

/**
 * The screen a path shows, for deciding whether the screen changed. An order's
 * own address opens its drawer OVER the Orders list (the same screen), so it is
 * not a page change: treating it as one slid the whole list in behind the
 * drawer and sent it back to the top, which is why the drawer felt rough.
 * Request of 5 October 2026.
 */
export function screenOf(path: string): string {
  const [first, second, third] = parts(path);
  if (first === 'orders' && second !== undefined && third === undefined) return '/orders';
  return path;
}

/**
 * Which way the screen moved, from the path before, the path after and how
 * the router got there. Section 3 of the request of 5 October 2026: deeper
 * slides in from the right and Back reverses it; another section is a
 * cross-fade.
 *
 * Safari's own swipe from the edge already slides the previous page in; adding
 * ours on top would play it twice, so a Back in the iPhone browser (not the
 * installed app, which has no such gesture) arrives with no animation.
 */
export function routeDirection(
  before: string | null,
  after: string,
  navigation: 'POP' | 'PUSH' | 'REPLACE',
  iPhoneBrowser = false,
): RouteDirection {
  if (before === null || screenOf(before) === screenOf(after)) return 'none';
  const from = parts(screenOf(before));
  const to = parts(screenOf(after));
  if (navigation === 'POP') return iPhoneBrowser ? 'none' : 'back';
  if (from[0] !== to[0]) return 'switch';
  if (to.length > from.length) return 'forward';
  if (to.length < from.length) return 'back';
  return 'switch';
}

/** Safari on an iPhone, outside the installed app: the one place with a swipe back of its own. */
export function isIPhoneBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  const standalone =
    (navigator as Navigator & { standalone?: boolean }).standalone === true ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches);
  return /iPhone|iPad|iPod/.test(navigator.userAgent) && !standalone;
}
